import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import PropTypes from 'prop-types';
import useMediaQuery from '@mui/material/useMediaQuery';
import { useTheme } from '@mui/material/styles';
import { useAuth } from '../context/AuthContext';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  Card,
  CardContent,
  CardHeader,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Divider,
  FormControl,
  FormControlLabel,
  Grid,
  IconButton,
  InputLabel,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  MenuItem,
  Paper,
  Select,
  Switch,
  TextField,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  Tabs,
  Tooltip,
  Typography,
  Collapse
} from '@mui/material';
import {
  ArrowBack,
  Business,
  DeviceHub,
  Bolt,
  WaterDrop,
  Speed,
  Settings,
  History,
  Warning,
  CheckCircle,
  Edit,
  Delete,
  LocationOn,
  CalendarToday,
  AccessTime,
  ExpandLess,
  ExpandMore,
  ArrowForward,
  ArrowUpward,
  ArrowDownward,
  MyLocation
} from '@mui/icons-material';
import {
  Bar,
  Line
} from 'react-chartjs-2';
import {
  Chart as ChartJS,
  LineController,
  BarController,
  CategoryScale,
  LinearScale,
  BarElement,
  PointElement,
  LineElement,
  Title,
  Tooltip as ChartTooltip,
  Legend
} from 'chart.js';
import {
  format,
  parseISO,
  subDays
} from 'date-fns';
import PermissionGuard from '../components/PermissionGuard';
import StructuredLocationFields from '../components/StructuredLocationFields';
import deviceService from '../services/deviceService';
import siteService from '../services/siteService';
import dashboardService from '../services/dashboardService';
import readingService from '../services/readingService';
import { useRefresh } from '../context/RefreshContext';
import { downloadFile } from '../utils/downloadHelper';

// Register ChartJS components
const barValueLabelsPlugin = {
  id: 'barValueLabelsV4',
  beforeDraw: (chart) => {
    const pluginOptions = chart?.config?.options?.plugins?.[barValueLabelsPlugin.id] ?? chart?.options?.plugins?.[barValueLabelsPlugin.id];
    if (!pluginOptions || pluginOptions.display === false) return;

    const stateBag = chart.$_barValueLabelsState || (chart.$_barValueLabelsState = {});
    stateBag[barValueLabelsPlugin.id] = {
      seenKeys: new Set()
    };
  },
  afterDatasetsDraw: (chart) => {
    const pluginOptions = chart?.config?.options?.plugins?.[barValueLabelsPlugin.id] ?? chart?.options?.plugins?.[barValueLabelsPlugin.id];
    if (!pluginOptions || pluginOptions.display === false) return;

    const stateBag = chart.$_barValueLabelsState || (chart.$_barValueLabelsState = {});
    const state = stateBag[barValueLabelsPlugin.id] || (stateBag[barValueLabelsPlugin.id] = { seenKeys: new Set() });

    const ctx = chart.ctx;
    const color = pluginOptions.color || '#333';
    const insideColor = pluginOptions.insideColor;
    const fontSize = pluginOptions.fontSize || 10;
    const fontFamily = pluginOptions.fontFamily;
    const offset = Number.isFinite(pluginOptions.offset) ? pluginOptions.offset : 4;
    const minValue = Number.isFinite(pluginOptions.minValue) ? pluginOptions.minValue : 0;
    const position = pluginOptions.position || 'top';
    const showOnlyExtremesOnMobile = pluginOptions.showOnlyExtremesOnMobile === true && pluginOptions.isMobile === true;
    const denseDataThreshold = Number.isFinite(pluginOptions.denseDataThreshold)
      ? Number(pluginOptions.denseDataThreshold)
      : 10;
    const formatter = typeof pluginOptions.formatter === 'function'
      ? pluginOptions.formatter
      : (v) => {
          const num = Number(v);
          if (!Number.isFinite(num)) return '';
          return Math.round(num).toLocaleString('en-US');
        };

    const drawDatasetLabels = (datasetIndex) => {
      const meta = chart.getDatasetMeta(datasetIndex);
      if (!meta || meta.hidden) return;
      if (meta.type !== 'bar') return;

      const rawData = Array.isArray(chart.data?.datasets?.[datasetIndex]?.data)
        ? chart.data.datasets[datasetIndex].data
        : [];
      const validIndices = [];
      rawData.forEach((raw, idx) => {
        const value = Number(raw);
        if (!Number.isFinite(value)) return;
        if (value <= minValue) return;
        validIndices.push(idx);
      });

      const shouldReduceLabels = showOnlyExtremesOnMobile && validIndices.length > denseDataThreshold;
      let allowedIndices = null;
      if (shouldReduceLabels) {
        let minIndex = validIndices[0];
        let maxIndex = validIndices[0];

        validIndices.forEach((idx) => {
          const value = Number(rawData[idx]);
          const minValueAtIdx = Number(rawData[minIndex]);
          const maxValueAtIdx = Number(rawData[maxIndex]);
          if (value < minValueAtIdx) minIndex = idx;
          if (value > maxValueAtIdx) maxIndex = idx;
        });

        allowedIndices = new Set([minIndex, maxIndex]);
      }

      meta.data.forEach((element, index) => {
        if (allowedIndices && !allowedIndices.has(index)) return;

        const raw = chart.data?.datasets?.[datasetIndex]?.data?.[index];
        const value = Number(raw);
        if (!Number.isFinite(value)) return;
        if (value <= minValue) return;

        const pos = element.tooltipPosition ? element.tooltipPosition() : { x: element.x, y: element.y };
        const label = formatter(value, { datasetIndex, index, chart });
        if (!label) return;

        const baseY = Number.isFinite(element?.base) ? element.base : pos.y;
        const barTop = Math.min(pos.y, baseY);
        const barBottom = Math.max(pos.y, baseY);
        const barHeight = barBottom - barTop;

        const seenKey = `${datasetIndex}:${index}:${label}:${Math.round(pos.x)}:${Math.round(pos.y)}`;
        if (state.seenKeys?.has(seenKey)) return;
        state.seenKeys?.add(seenKey);

        let x = pos.x;
        let y = pos.y - offset;
        let fill = color;
        let baseline = 'bottom';

        if (position === 'middle') {
          if (barHeight < fontSize + 6) {
            y = barBottom + offset;
            baseline = 'top';
          } else {
            y = barTop + (barHeight / 2);
            baseline = 'middle';
            fill = insideColor || color;
          }
        } else if (position === 'insideTop') {
          if (barHeight < fontSize + 6) {
            y = barTop - offset;
            baseline = 'bottom';
          } else {
            y = barTop + 2;
            baseline = 'top';
            fill = insideColor || color;
          }
        } else if (position === 'inside') {
          if (barHeight < fontSize + 6) {
            y = barBottom + offset;
            baseline = 'top';
          } else {
            y = barTop + 2;
            baseline = 'top';
            fill = insideColor || color;
          }
        } else if (position === 'bottom') {
          y = barBottom + offset;
          baseline = 'top';
        } else {
          y = barTop - offset;
          baseline = 'bottom';
        }

        ctx.save();
        ctx.fillStyle = fill;
        ctx.textAlign = 'center';
        ctx.textBaseline = baseline;
        ctx.font = `${fontSize}px ${fontFamily || 'sans-serif'}`;
        ctx.fillText(label, x, y);
        ctx.restore();
      });
    };

    for (let i = 0; i < (chart.data?.datasets?.length || 0); i++) {
      drawDatasetLabels(i);
    }
  }
};

const ensureBarValueLabelsPluginRegistered = () => {
  if (ChartJS.__barValueLabelsV4PluginRegistered) return;
  ChartJS.register(barValueLabelsPlugin);
  ChartJS.__barValueLabelsV4PluginRegistered = true;
};

ChartJS.register(
  LineController,
  BarController,
  CategoryScale,
  LinearScale,
  BarElement,
  PointElement,
  LineElement,
  Title,
  ChartTooltip,
  Legend
);

ensureBarValueLabelsPluginRegistered();

// Helper function for tab accessibility props
function a11yProps(index) {
  return {
    id: `device-tab-${index}`,
    'aria-controls': `device-tabpanel-${index}`,
  };
}

// TabPanel component for tab content
function TabPanel(props) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`device-tabpanel-${index}`}
      aria-labelledby={`device-tab-${index}`}
      {...other}
    >
      {value === index && (
        <Box sx={{ p: { xs: 1, sm: 2 } }}>
          {children}
        </Box>
      )}
    </div>
  );
}

TabPanel.propTypes = {
  children: PropTypes.node,
  index: PropTypes.number.isRequired,
  value: PropTypes.number.isRequired,
};

const DeviceDetails = () => {
  const { id: deviceId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const { hasPermission, user } = useAuth();
  const { refreshCounter, refreshData } = useRefresh(); // Subscribe to refreshCounter

  const chartTextColor = theme.palette.mode === 'dark'
    ? 'rgba(255, 255, 255, 0.92)'
    : theme.palette.text.primary;
  const chartGridColor = theme.palette.mode === 'dark'
    ? 'rgba(255, 255, 255, 0.16)'
    : theme.palette.divider;
  const chartTooltipBgColor = theme.palette.mode === 'dark'
    ? 'rgba(33, 33, 33, 0.92)'
    : 'rgba(255, 255, 255, 0.98)';
  const chartTooltipTextColor = theme.palette.mode === 'dark'
    ? 'rgba(255, 255, 255, 0.92)'
    : 'rgba(0, 0, 0, 0.87)';
  const chartTooltipBorderColor = theme.palette.mode === 'dark'
    ? 'rgba(255, 255, 255, 0.18)'
    : 'rgba(0, 0, 0, 0.12)';
  const isSmallScreen = useMediaQuery('(max-width:900px)');
  const iconSize = isSmallScreen ? '1.25rem' : '1.5rem';
  const [devicePixelRatio, setDevicePixelRatio] = useState(() => {
    if (typeof window === 'undefined') return 1;
    return window.devicePixelRatio || 1;
  });
  const [loading, setLoading] = useState(true);
  const [device, setDevice] = useState(null);
  const [site, setSite] = useState(null);
  const [readings, setReadings] = useState([]);
  const [paginatedReadings, setPaginatedReadings] = useState([]);
  const [readingsPage, setReadingsPage] = useState(0);
  const [readingsRowsPerPage, setReadingsRowsPerPage] = useState(50);
  const [totalReadings, setTotalReadings] = useState(0);
  const [paginatedReadingsLoading, setPaginatedReadingsLoading] = useState(false);
  const [readingEditOpen, setReadingEditOpen] = useState(false);
  const [readingDeleteOpen, setReadingDeleteOpen] = useState(false);
  const [readingDeleteAllOpen, setReadingDeleteAllOpen] = useState(false);
  const [readingSaving, setReadingSaving] = useState(false);
  const [deleteReadingsMode, setDeleteReadingsMode] = useState('all');
  const [deleteReadingsBeforeDate, setDeleteReadingsBeforeDate] = useState('');
  const [deleteReadingsStartDate, setDeleteReadingsStartDate] = useState('');
  const [deleteReadingsEndDate, setDeleteReadingsEndDate] = useState('');
  const [selectedReading, setSelectedReading] = useState(null);
  const [editReadingValue, setEditReadingValue] = useState('');
  const [editReadingTime, setEditReadingTime] = useState('');
  const [dailyUsage, setDailyUsage] = useState([]);
  const [dailyReadingCounts, setDailyReadingCounts] = useState([]);
  const [monthlyUsage, setMonthlyUsage] = useState([]);
  const [hourlyUsage, setHourlyUsage] = useState(Array(24).fill(0));
  const [yesterdayHourlyUsage, setYesterdayHourlyUsage] = useState(Array(24).fill(0));
  const [todayUsage, setTodayUsage] = useState(0);
  const [yesterdayUsage, setYesterdayUsage] = useState(0);
  const [last30DaysUsage, setLast30DaysUsage] = useState(0);
  const [error, setError] = useState(null);
  const [tabValue, setTabValue] = useState(0);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [savingEditDialog, setSavingEditDialog] = useState(false);
  const [gpsLocating, setGpsLocating] = useState(false);
  const [sites, setSites] = useState([]);
  const [parentDeviceOptions, setParentDeviceOptions] = useState([]);
  const [parentDeviceOptionsLoading, setParentDeviceOptionsLoading] = useState(false);
  const [deviceFormData, setDeviceFormData] = useState({
    serialNumber: '',
    accountNumber: '',
    description: '',
    location: '',
    latitude: '',
    longitude: '',
    siteIds: [],
    parentId: '',
    deviceType: 'water_meter',
    isActive: true,
    multiplier: 1,
    highUsageThresholdLiters: '2',
    dailyThresholdLiters: '',
    decryptKey: ''
  });

  const [multiplierPromptOpen, setMultiplierPromptOpen] = useState(false);
  const [multiplierPromptSaving, setMultiplierPromptSaving] = useState(false);
  const [multiplierApplyMode, setMultiplierApplyMode] = useState('');
  const [multiplierEarliestDate, setMultiplierEarliestDate] = useState('');
  const [multiplierCustomDate, setMultiplierCustomDate] = useState('');
  const [multiplierCustomEndDate, setMultiplierCustomEndDate] = useState('');
  const [recalculateUsageSaving, setRecalculateUsageSaving] = useState(false);
  const [pendingUpdatePayload, setPendingUpdatePayload] = useState(null);
  const [multiplierPreview, setMultiplierPreview] = useState({
    loading: false,
    error: '',
    previousMultiplier: 1,
    nextMultiplier: 1,
    rows: []
  });
  const [deviceHierarchy, setDeviceHierarchy] = useState(null);
  const [hierarchyLoading, setHierarchyLoading] = useState(false);
  const [expandedItems, setExpandedItems] = useState({});
  const [startDate, setStartDate] = useState(''); // YYYY-MM-DD
  const [endDate, setEndDate] = useState('');   // YYYY-MM-DD

  useEffect(() => {
    if (typeof window === 'undefined') return undefined;

    const updateDpr = () => {
      const next = window.devicePixelRatio || 1;
      setDevicePixelRatio((prev) => (Math.abs(prev - next) < 0.001 ? prev : next));
    };

    let mql;
    let mqlListener;
    const intervalId = window.setInterval(updateDpr, 500);

    const bindResolutionListener = () => {
      try {
        const dpr = window.devicePixelRatio || 1;
        mql = window.matchMedia?.(`(resolution: ${dpr}dppx)`);
        if (!mql) return;

        mqlListener = () => {
          updateDpr();
          if (mql) {
            if (typeof mql.removeEventListener === 'function') {
              mql.removeEventListener('change', mqlListener);
            } else if (typeof mql.removeListener === 'function') {
              mql.removeListener(mqlListener);
            }
          }
          bindResolutionListener();
        };

        if (typeof mql.addEventListener === 'function') {
          mql.addEventListener('change', mqlListener);
        } else if (typeof mql.addListener === 'function') {
          mql.addListener(mqlListener);
        }
      } catch (e) {
        // ignore
      }
    };

    window.addEventListener('resize', updateDpr);
    window.visualViewport?.addEventListener('resize', updateDpr);
    window.addEventListener('focus', updateDpr);
    document.addEventListener('visibilitychange', updateDpr);
    bindResolutionListener();
    return () => {
      window.removeEventListener('resize', updateDpr);
      window.visualViewport?.removeEventListener('resize', updateDpr);
      window.removeEventListener('focus', updateDpr);
      document.removeEventListener('visibilitychange', updateDpr);
      window.clearInterval(intervalId);
      if (mql && mqlListener) {
        if (typeof mql.removeEventListener === 'function') {
          mql.removeEventListener('change', mqlListener);
        } else if (typeof mql.removeListener === 'function') {
          mql.removeListener(mqlListener);
        }
      }
    };
  }, []);

  // Chart references to handle cleanup
  const dailyChartRef = useRef(null);
  const hourlyChartRef = useRef(null);
  const todayHourlyChartRef = useRef(null);
  const yesterdayHourlyChartRef = useRef(null);
  
  // Units and colors by device type
  const isElectrical = device?.deviceType === 'electrical_meter';
  const unit = isElectrical ? 'kWh' : 'L';
  const colorPrimaryBg = isElectrical ? 'rgba(255, 159, 64, 0.85)' : 'rgba(54, 162, 235, 0.8)';
  const colorPrimaryBorder = isElectrical ? 'rgb(255, 159, 64)' : 'rgb(54, 162, 235)';
  const colorTodayBg = isElectrical ? 'rgba(255, 193, 7, 0.8)' : 'rgba(75, 192, 192, 0.8)';
  const colorTodayBorder = isElectrical ? 'rgb(255, 193, 7)' : 'rgb(75, 192, 192)';
  const colorYestBg = isElectrical ? 'rgba(255, 87, 34, 0.8)' : 'rgba(255, 99, 132, 0.8)';
  const colorYestBorder = isElectrical ? 'rgb(255, 87, 34)' : 'rgb(255, 99, 132)';
  // Monthly chart colors should be different from the daily chart colors
  const colorMonthlyBg = isElectrical ? 'rgba(75, 192, 192, 0.8)' : 'rgba(153, 102, 255, 0.8)';
  const colorMonthlyBorder = isElectrical ? 'rgb(75, 192, 192)' : 'rgb(153, 102, 255)';

  const isAdmin = user?.role === 'admin';
  const isAdminOrOperator = isAdmin || user?.role === 'operator';
  const isAdminOrSuper = isAdminOrOperator || user?.role === 'super_user';
  const isEndUser = user?.role === 'end_user';

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const list = await siteService.fetchSites();
        if (cancelled) return;
        setSites(Array.isArray(list) ? list : []);
      } catch (e) {
        if (cancelled) return;
        setSites([]);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    if (isEndUser) return undefined;
    let cancelled = false;
    (async () => {
      try {
        setParentDeviceOptionsLoading(true);
        const resp = await deviceService.fetchDevices({ limit: 10000, lean: true });
        if (cancelled) return;
        setParentDeviceOptions(resp?.devices || []);
      } catch (e) {
        if (cancelled) return;
        setParentDeviceOptions([]);
      } finally {
        if (cancelled) return;
        setParentDeviceOptionsLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [isEndUser]);

  const backButtonLabel = (() => {
    const returnTo = location?.state?.returnTo;
    if (typeof returnTo === 'string' && returnTo.startsWith('/sites/')) return 'Back to Site';
    return 'Back to Devices';
  })();

  // Function to render device hierarchy item
  const renderDeviceHierarchyItem = (device) => {
    if (!device) return null;
    
    return (
      <ListItem key={device.id}>
        <ListItemButton onClick={() => navigate(`/devices/${device.id}`, { state: location?.state })}>
          <ListItemIcon>
            <ArrowUpwardIcon color="primary" />
          </ListItemIcon>
          <ListItemText 
            primary={device.description || device.serialNumber}
            secondary={device.location || 'No location specified'}
          />
        </ListItemButton>
        {device.children && device.children.length > 0 && (
          <List>
            {device.children.map(child => renderDeviceHierarchyItem(child))}
          </List>
        )}
      </ListItem>
    );
  };

  useEffect(() => {
    const loadDeviceData = async () => {
      setLoading(true);
      setError(null);
      try {
        // Fetch device details from the API
        console.log(`Fetching device with ID: ${deviceId}`);
        const deviceData = await deviceService.fetchDevice(deviceId);

        if (!deviceData) {
          setError('Device not found');
          setLoading(false);
          return;
        }

        setDevice(deviceData);

        // If the device has a siteId, fetch the site details
        if (deviceData.sites && deviceData.sites.length > 0) {
          try {
            const siteData = await siteService.fetchSite(deviceData.sites[0].id);
            setSite(siteData);
          } catch (error) {
            console.error('Error loading site data:', error);
            // Don't set an error for the whole page if just the site data fails
          }
        }

        // Fetch device readings
        try {
          const readingsData = await deviceService.fetchDeviceReadings(deviceId);

          if (readingsData && Array.isArray(readingsData)) {
            setReadings(readingsData);
            // Compute usage from scaled usageSinceLastReading
            const sorted = [...readingsData].filter(r => r.readingTime).sort((a,b) => new Date(a.readingTime) - new Date(b.readingTime));
            const dailyUsageMap = {};
            for (let i = 1; i < sorted.length; i++) {
              const prev = sorted[i - 1];
              const curr = sorted[i];
              const dateStr = curr.readingTime.split('T')[0];
              const usage = Math.max(0, Number(curr.usageSinceLastReading) || 0);
              if (!dailyUsageMap[dateStr]) {
                dailyUsageMap[dateStr] = { date: dateStr, totalUsage: 0, readingCount: 0 };
              }
              dailyUsageMap[dateStr].totalUsage += usage;
              dailyUsageMap[dateStr].readingCount += 1;
            }

            const dailyUsageData = Object.values(dailyUsageMap).sort((a, b) => new Date(a.date) - new Date(b.date)).map(item => ({
              date: item.date,
              totalUsage: item.totalUsage,
              averageUsage: item.totalUsage / Math.max(1, item.readingCount)
            }));
            const dailyReadingCountsData = Object.values(dailyUsageMap).sort((a, b) => new Date(a.date) - new Date(b.date)).map(item => ({ date: item.date, count: item.readingCount }));
            setDailyUsage(dailyUsageData);
            setDailyReadingCounts(dailyReadingCountsData);

            const today = new Date().toISOString().split('T')[0];
            const yesterday = new Date(Date.now() - 86400000).toISOString().split('T')[0];
            setTodayUsage(dailyUsageMap[today]?.totalUsage || 0);
            setYesterdayUsage(dailyUsageMap[yesterday]?.totalUsage || 0);
            let total30Days = 0;
            const thirtyDaysAgo = new Date(Date.now() - 30 * 86400000).toISOString().split('T')[0];
            Object.values(dailyUsageMap).forEach(item => { if (item.date >= thirtyDaysAgo) total30Days += item.totalUsage; });
            setLast30DaysUsage(total30Days);

            // Hourly usage from scaled usageSinceLastReading
            const todayHourlyData = Array(24).fill(0);
            const yesterdayHourlyData = Array(24).fill(0);
            for (let i = 1; i < sorted.length; i++) {
              const prev = sorted[i - 1];
              const curr = sorted[i];
              const usage = Math.max(0, Number(curr.usageSinceLastReading) || 0);
              const dStr = curr.readingTime.split('T')[0];
              const hr = new Date(curr.readingTime).getHours();
              if (dStr === today) todayHourlyData[hr] += usage; else if (dStr === yesterday) yesterdayHourlyData[hr] += usage;
            }
            setHourlyUsage(todayHourlyData);
            setYesterdayHourlyUsage(yesterdayHourlyData);
            console.log('🔍 DEVICE PAGE - Today hourly usage (diff-based):', todayHourlyData);
            console.log('🔍 DEVICE PAGE - Yesterday hourly usage (diff-based):', yesterdayHourlyData);
            console.log('🔍 DEVICE PAGE - Total readings processed:', readingsData.length);

          } else {
            console.warn('Readings data is not in expected format:', readingsData);
            setReadings([]);
            setDailyUsage([]);
          }
        } catch (error) {
          console.error('Error loading device readings:', error);
          setReadings([]);
          setDailyUsage([]);
          // Don't set an error for the whole page if just the readings data fails
        }

        // Fetch authoritative daily usage from API for last 12 months to power charts and totals
        try {
          const endDateStr = new Date().toISOString().split('T')[0];
          const startDateObj = new Date();
          startDateObj.setMonth(startDateObj.getMonth() - 12);
          const startDateStr = startDateObj.toISOString().split('T')[0];
          const apiDaily = await deviceService.getDeviceDailyUsage(deviceId, { startDate: startDateStr, endDate: endDateStr });
          const normalized = Array.isArray(apiDaily) ? apiDaily.map(d => ({
            date: d.date,
            totalUsage: (Number(d.totalUsage) || 0),
            readingCount: Number(d.readingCount) || 0
          })) : [];
          // Update daily (last 30 days)
          const thirtyDaysAgo = new Date(Date.now() - 30 * 86400000).toISOString().split('T')[0];
          const last30 = normalized.filter(d => d.date >= thirtyDaysAgo).sort((a,b) => new Date(a.date) - new Date(b.date));
          if (last30.length > 0) {
            setDailyUsage(last30.map(d => ({ date: d.date, totalUsage: d.totalUsage, averageUsage: d.readingCount > 0 ? d.totalUsage / Math.max(1, d.readingCount) : 0 })));
            setDailyReadingCounts(last30.map(d => ({ date: d.date, count: d.readingCount })));
            setLast30DaysUsage(last30.reduce((s, d) => s + d.totalUsage, 0));
            const todayStr = new Date().toISOString().split('T')[0];
            const yesterdayStr = new Date(Date.now() - 86400000).toISOString().split('T')[0];
            setTodayUsage(last30.find(d => d.date === todayStr)?.totalUsage || 0);
            setYesterdayUsage(last30.find(d => d.date === yesterdayStr)?.totalUsage || 0);
          }
          // Build monthly aggregate for last 12 months
          const byMonth = new Map();
          normalized.forEach(d => {
            const m = d.date.slice(0,7);
            byMonth.set(m, (byMonth.get(m) || 0) + (Number(d.totalUsage) || 0));
          });
          const months = [];
          const cursor = new Date();
          cursor.setMonth(cursor.getMonth() - 11);
          cursor.setDate(1);
          for (let i = 0; i < 12; i++) {
            const key = `${cursor.getFullYear()}-${String(cursor.getMonth()+1).padStart(2,'0')}`;
            months.push({ month: key, total: byMonth.get(key) || 0 });
            cursor.setMonth(cursor.getMonth() + 1);
          }
          setMonthlyUsage(months);
        } catch (e) {
          console.error('Error loading device daily usage via API:', e);
        }
      } catch (error) {
        console.error('Error loading device data:', error);
        setDevice(null);
        setError(`Device not found or could not be loaded: ${error.message || 'Unknown error'}`);
      } finally {
        setLoading(false);
      }
    };

    if (deviceId) {
      loadDeviceData();
    } else {
      setError('No device ID provided');
      setLoading(false);
    }
  }, [deviceId, refreshCounter]);

  useEffect(() => {
    // no multiplier history display on this page
  }, [isAdminOrSuper, deviceId]);

  // Removed: device hierarchy loading tied to the removed Connections tab

  // Handle tab change
  const handleTabChange = (event, newValue) => {
    setTabValue(newValue);
  };

  // Handle back button click
  const handleBackClick = () => {
    const returnTo = location?.state?.returnTo;
    const returnState = location?.state?.returnState;
    const returnScrollY = location?.state?.returnScrollY;
    if (returnTo) {
      navigate(returnTo, {
        state: {
          restore: returnState || null,
          restoreScrollY: typeof returnScrollY === 'number' ? returnScrollY : 0
        }
      });
      return;
    }
    navigate('/devices');
  };

  const handleDeviceFormChange = (event) => {
    const { name, value } = event.target;
    setDeviceFormData({
      ...deviceFormData,
      [name]: value
    });
  };

  const handleSiteSelection = (event, newValue) => {
    setDeviceFormData({
      ...deviceFormData,
      siteIds: Array.isArray(newValue) ? newValue.map((s) => s.id) : []
    });
  };

  const handleParentDeviceSelection = (event, newValue) => {
    setDeviceFormData({
      ...deviceFormData,
      parentId: newValue ? newValue.id : null
    });
  };

  const handleUseCurrentGps = async () => {
    if (gpsLocating) return;

    try {
      if (!navigator.geolocation) {
        return;
      }

      setGpsLocating(true);

      const position = await new Promise((resolve, reject) => {
        navigator.geolocation.getCurrentPosition(resolve, reject, {
          enableHighAccuracy: true,
          timeout: 15000,
          maximumAge: 0
        });
      });

      const lat = position?.coords?.latitude;
      const lng = position?.coords?.longitude;

      if (typeof lat !== 'number' || typeof lng !== 'number') {
        return;
      }

      setDeviceFormData((prev) => ({
        ...prev,
        latitude: lat.toFixed(7),
        longitude: lng.toFixed(7)
      }));
    } catch (e) {
      // ignore
    } finally {
      setGpsLocating(false);
    }
  };

  const handleRecalculateUsage = async () => {
    try {
      if (!deviceId) return;
      setRecalculateUsageSaving(true);
      const result = await deviceService.recalculateDeviceUsage(deviceId);
      const msg = `Usage recalculated. Updated readings: ${Number(result?.repairedReadings || 0)}, daily usage days: ${Number(result?.dailyUsageDays || 0)}`;
      alert(msg);
      refreshData();
    } catch (error) {
      console.error('Error recalculating device usage:', error);
      alert(`Error recalculating usage: ${error.message || 'Unknown error'}`);
    } finally {
      setRecalculateUsageSaving(false);
    }
  };

  // Handle edit device button click
  const handleEditDevice = () => {
    const siteIds = Array.isArray(device?.sites) ? device.sites.map((s) => s.id) : [];

    setDeviceFormData({
      serialNumber: device?.serialNumber || '',
      accountNumber: device?.accountNumber || device?.metadata?.accountNumber || '',
      description: device?.description || '',
      location: device?.location || '',
      latitude: device?.latitude !== undefined && device?.latitude !== null ? String(device.latitude) : '',
      longitude: device?.longitude !== undefined && device?.longitude !== null ? String(device.longitude) : '',
      siteIds: siteIds,
      parentId: device?.parentId || null,
      deviceType: device?.deviceType || 'water_meter',
      isActive: device?.isActive !== false,
      multiplier: device?.multiplier !== undefined && device?.multiplier !== null ? Number(device.multiplier) : 1,
      highUsageThresholdLiters: device?.highUsageThresholdLiters !== undefined && device?.highUsageThresholdLiters !== null ? Number(device.highUsageThresholdLiters) : '2',
      dailyThresholdLiters: device?.dailyThresholdLiters !== undefined && device?.dailyThresholdLiters !== null ? Number(device.dailyThresholdLiters) : '',
      decryptKey: device?.metadata?.decryptKey || ''
    });

    setEditDialogOpen(true);
  };

  // Handle delete device dialog open
  const handleDeleteDialogOpen = () => {
    if (!isAdmin) return;
    setDeleteDialogOpen(true);
  };

  const handleCloseEditDialog = () => {
    if (savingEditDialog) return;
    setEditDialogOpen(false);
  };

  const handleCloseMultiplierPrompt = () => {
    if (multiplierPromptSaving) return;
    setMultiplierPromptOpen(false);
    setPendingUpdatePayload(null);
    setMultiplierApplyMode('');
    setMultiplierEarliestDate('');
    setMultiplierCustomDate('');
    setMultiplierCustomEndDate('');
    setMultiplierPreview({
      loading: false,
      error: '',
      previousMultiplier: 1,
      nextMultiplier: 1,
      rows: []
    });
  };

  const loadMultiplierPreview = async (targetDeviceId, previousMultiplier, nextMultiplier) => {
    setMultiplierPreview({
      loading: true,
      error: '',
      previousMultiplier,
      nextMultiplier,
      rows: []
    });

    try {
      const readings = await deviceService.fetchDeviceReadings(targetDeviceId, { limit: 2, order: 'desc' });
      const latestTwo = Array.isArray(readings)
        ? [...readings]
            .filter((r) => r && r.readingTime)
            .sort((a, b) => new Date(a.readingTime) - new Date(b.readingTime))
            .slice(-2)
        : [];

      if (latestTwo.length < 2) {
        setMultiplierPreview({
          loading: false,
          error: 'Not enough readings to preview (need at least 2 readings).',
          previousMultiplier,
          nextMultiplier,
          rows: []
        });
        return;
      }

      const [fromReading, toReading] = latestTwo;
      const rawUsage = Math.max(0, (Number(toReading.reading) || 0) - (Number(fromReading.reading) || 0));
      const previousUsage = Number((rawUsage * previousMultiplier).toFixed(3));
      const nextUsage = Number((rawUsage * nextMultiplier).toFixed(3));

      setMultiplierPreview({
        loading: false,
        error: '',
        previousMultiplier,
        nextMultiplier,
        rows: [
          {
            fromReadingTime: fromReading.readingTime,
            toReadingTime: toReading.readingTime,
            fromReadingValue: Number(fromReading.reading) || 0,
            toReadingValue: Number(toReading.reading) || 0,
            rawUsage,
            previousUsage,
            nextUsage
          }
        ]
      });
    } catch (error) {
      setMultiplierPreview({
        loading: false,
        error: 'Unable to load preview readings.',
        previousMultiplier,
        nextMultiplier,
        rows: []
      });
    }
  };

  const handleConfirmMultiplierPrompt = async () => {
    try {
      if (!multiplierApplyMode) return;
      if (!pendingUpdatePayload || !deviceId) return;
      setMultiplierPromptSaving(true);

      const todayStr = new Date().toISOString().split('T')[0];
      let effectiveFrom = todayStr;

      if (multiplierApplyMode === 'retroactive') {
        if (multiplierEarliestDate) {
          effectiveFrom = multiplierEarliestDate;
        } else {
          try {
            const earliest = await deviceService.fetchDeviceReadings(deviceId, { limit: 1, order: 'asc' });
            const rt = Array.isArray(earliest) && earliest[0]?.readingTime ? String(earliest[0].readingTime) : '';
            effectiveFrom = rt ? rt.split('T')[0] : todayStr;
          } catch (e) {
            effectiveFrom = todayStr;
          }
        }
      } else if (multiplierApplyMode === 'custom') {
        effectiveFrom = multiplierCustomDate || todayStr;
      }

      const payload = {
        ...pendingUpdatePayload,
        multiplierEffectiveFrom: effectiveFrom,
        multiplierEffectiveTo: multiplierCustomEndDate || undefined
      };

      const updatedDevice = await deviceService.updateDevice(deviceId, payload);
      setDevice((prev) => ({ ...(prev || {}), ...(updatedDevice || {}) }));
      setMultiplierPromptOpen(false);
      setPendingUpdatePayload(null);
      setEditDialogOpen(false);
      refreshData();
    } catch (error) {
      console.error('Error updating device multiplier:', error);
    } finally {
      setMultiplierPromptSaving(false);
    }
  };

  const handleSaveEditDevice = async () => {
    try {
      if (!deviceId) return;
      setSavingEditDialog(true);

      const thresholdValue = deviceFormData.dailyThresholdLiters === '' || deviceFormData.dailyThresholdLiters === null || deviceFormData.dailyThresholdLiters === undefined
        ? null
        : Number(deviceFormData.dailyThresholdLiters);

      const highUsageValue = deviceFormData.highUsageThresholdLiters === '' || deviceFormData.highUsageThresholdLiters === null || deviceFormData.highUsageThresholdLiters === undefined
        ? null
        : Number(deviceFormData.highUsageThresholdLiters);

      const latitude = deviceFormData.latitude === '' || deviceFormData.latitude === null || deviceFormData.latitude === undefined
        ? null
        : Number(deviceFormData.latitude);
      const longitude = deviceFormData.longitude === '' || deviceFormData.longitude === null || deviceFormData.longitude === undefined
        ? null
        : Number(deviceFormData.longitude);

      const apiData = isEndUser
        ? { dailyThresholdLiters: thresholdValue }
        : isAdminOrOperator
        ? {
            ...deviceFormData,
            multiplier: deviceFormData.multiplier !== undefined && deviceFormData.multiplier !== null ? Number(deviceFormData.multiplier) : 1,
            latitude,
            longitude,
            highUsageThresholdLiters: highUsageValue,
            dailyThresholdLiters: thresholdValue,
            parentId: deviceFormData.parentId || null,
            siteIds: Array.isArray(deviceFormData.siteIds) ? deviceFormData.siteIds : [],
            metadata: String(deviceFormData.decryptKey || '').trim() ? { decryptKey: String(deviceFormData.decryptKey || '').trim() } : {}
          }
        : {
            description: deviceFormData.description,
            location: deviceFormData.location,
            latitude,
            longitude,
            accountNumber: deviceFormData.accountNumber || null,
            highUsageThresholdLiters: highUsageValue,
            dailyThresholdLiters: thresholdValue
          };

      if (isAdminOrOperator) {
        const prevMult = device?.multiplier !== undefined && device?.multiplier !== null ? Number(device.multiplier) : 1;
        const nextMult = apiData.multiplier !== undefined && apiData.multiplier !== null ? Number(apiData.multiplier) : 1;

        if (Math.abs(prevMult - nextMult) > 1e-12) {
          setPendingUpdatePayload(apiData);
          setMultiplierApplyMode('');
          setMultiplierEarliestDate('');
          setMultiplierPromptOpen(true);
          loadMultiplierPreview(deviceId, prevMult, nextMult);

          (async () => {
            try {
              const earliest = await deviceService.fetchDeviceReadings(deviceId, { limit: 1, order: 'asc' });
              const rt = Array.isArray(earliest) && earliest[0]?.readingTime ? String(earliest[0].readingTime) : '';
              const d = rt ? rt.split('T')[0] : '';
              setMultiplierEarliestDate(d);
            } catch (e) {
              setMultiplierEarliestDate('');
            }
          })();

          return;
        }
      }

      const updated = await deviceService.updateDevice(deviceId, apiData);
      setDevice((prev) => ({ ...(prev || {}), ...(updated || {}) }));
      setEditDialogOpen(false);
      refreshData();
    } catch (error) {
      console.error('Error updating device:', error);
    } finally {
      setSavingEditDialog(false);
    }
  };

  // Handle delete device dialog close
  const handleDeleteDialogClose = () => {
    setDeleteDialogOpen(false);
  };

  // Handle delete device
  const handleDeleteDevice = async () => {
    try {
      await deviceService.deleteDevice(deviceId);
      // Navigate back to originating page if available
      handleBackClick();
    } catch (error) {
      console.error('Error deleting device:', error);
      // Close dialog
      setDeleteDialogOpen(false);
    }
  };

  // Toggle expanded state for a device in the hierarchy view
  const handleToggleExpand = (deviceId) => {
    setExpandedItems(prev => ({
      ...prev,
      [deviceId]: !prev[deviceId]
    }));
  };

  // Navigate to a device in the hierarchy
  const handleDeviceClick = (deviceId) => {
    navigate(`/devices/${deviceId}`, { state: location?.state });
  };

  // Format date for display
  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    try {
      return new Date(dateString).toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      });
    } catch (error) {
      console.error('Error formatting date:', error);
      return dateString;
    }
  };

  // Format number for display
  const formatNumber = (value) => {
    if (value === null || value === undefined) return '0';
    return parseFloat(value).toLocaleString('en-US', {
      maximumFractionDigits: 2,
      minimumFractionDigits: 0
    });
  };

  const canManageReadings = isAdminOrOperator;
  const canDeleteReadings = isAdmin;

  const fetchPaginatedReadings = useCallback(async (page = 0, rowsPerPage = 50, start = '', end = '') => {
    if (!deviceId) return;
    setPaginatedReadingsLoading(true);
    try {
      const offset = page * rowsPerPage;
      const data = await deviceService.fetchDeviceReadings(deviceId, {
        startDate: start || undefined,
        endDate: end || undefined,
        limit: rowsPerPage,
        offset,
        order: 'desc',
        includeTotal: true
      });
      if (data && data.readings && Array.isArray(data.readings)) {
        setPaginatedReadings(data.readings);
        setTotalReadings(Number(data.total) || data.readings.length);
      } else if (Array.isArray(data)) {
        setPaginatedReadings(data);
        setTotalReadings(data.length);
      } else {
        setPaginatedReadings([]);
        setTotalReadings(0);
      }
    } catch (error) {
      console.error('Error fetching paginated readings:', error);
      setPaginatedReadings([]);
      setTotalReadings(0);
    } finally {
      setPaginatedReadingsLoading(false);
    }
  }, [deviceId]);

  const refreshDeviceReadings = async () => {
    // Refresh summary readings for charts and recent readings card
    const readingsData = await deviceService.fetchDeviceReadings(deviceId, { limit: 100, order: 'desc' });
    if (readingsData && Array.isArray(readingsData)) {
      setReadings(readingsData);
    } else if (readingsData && Array.isArray(readingsData.readings)) {
      setReadings(readingsData.readings);
    } else {
      setReadings([]);
    }
    // Refresh the paginated table for the current filter/page
    await fetchPaginatedReadings(readingsPage, readingsRowsPerPage, startDate, endDate);
  };

  useEffect(() => {
    if (!device || !device.id) return;
    fetchPaginatedReadings(readingsPage, readingsRowsPerPage, startDate, endDate);
  }, [device, fetchPaginatedReadings, readingsPage, readingsRowsPerPage, startDate, endDate]);

  const openEditReading = (reading) => {
    setSelectedReading(reading);
    setEditReadingValue(reading?.reading != null ? String(reading.reading) : '');
    setEditReadingTime(reading?.readingTime ? String(reading.readingTime).slice(0, 16) : '');
    setReadingEditOpen(true);
  };

  const openDeleteReading = (reading) => {
    setSelectedReading(reading);
    setReadingDeleteOpen(true);
  };

  const openDeleteAllReadings = () => {
    if (startDate && endDate) {
      setDeleteReadingsMode('between');
      setDeleteReadingsStartDate(startDate);
      setDeleteReadingsEndDate(endDate);
      setDeleteReadingsBeforeDate('');
    } else if (endDate) {
      setDeleteReadingsMode('before');
      setDeleteReadingsBeforeDate(endDate);
      setDeleteReadingsStartDate('');
      setDeleteReadingsEndDate('');
    } else {
      setDeleteReadingsMode('all');
      setDeleteReadingsBeforeDate('');
      setDeleteReadingsStartDate('');
      setDeleteReadingsEndDate('');
    }
    setReadingDeleteAllOpen(true);
  };

  const closeReadingDialogs = () => {
    setReadingEditOpen(false);
    setReadingDeleteOpen(false);
    setReadingDeleteAllOpen(false);
    setDeleteReadingsMode('all');
    setDeleteReadingsBeforeDate('');
    setDeleteReadingsStartDate('');
    setDeleteReadingsEndDate('');
    setSelectedReading(null);
  };

  const handleSaveReadingEdit = async () => {
    if (!selectedReading?.id) return;
    setReadingSaving(true);
    try {
      const payload = {};
      if (editReadingValue !== '') payload.reading = Number(editReadingValue);
      if (editReadingTime) payload.readingTime = new Date(editReadingTime).toISOString();
      await readingService.updateReading(selectedReading.id, payload);
      await refreshDeviceReadings();
      closeReadingDialogs();
    } finally {
      setReadingSaving(false);
    }
  };

  const handleConfirmReadingDelete = async () => {
    if (!selectedReading?.id) return;
    setReadingSaving(true);
    try {
      await readingService.deleteReading(selectedReading.id);
      await refreshDeviceReadings();
      closeReadingDialogs();
    } finally {
      setReadingSaving(false);
    }
  };

  const handleConfirmDeleteAllReadings = async () => {
    if (!deviceId) return;

    const mode = deleteReadingsMode || 'all';
    const isBeforeModeValid = mode !== 'before' || Boolean(deleteReadingsBeforeDate);
    const isBetweenModeValid = mode !== 'between' || (
      Boolean(deleteReadingsStartDate)
      && Boolean(deleteReadingsEndDate)
      && deleteReadingsEndDate >= deleteReadingsStartDate
    );

    if (!isBeforeModeValid || !isBetweenModeValid) {
      setError('Please provide a valid date scope for deleting readings.');
      return;
    }

    const deleteOptions = { mode };
    if (mode === 'before') {
      deleteOptions.beforeDate = deleteReadingsBeforeDate;
    }
    if (mode === 'between') {
      deleteOptions.startDate = deleteReadingsStartDate;
      deleteOptions.endDate = deleteReadingsEndDate;
    }

    setReadingSaving(true);
    try {
      await readingService.deleteAllReadingsForDevice(deviceId, deleteOptions);
      await refreshDeviceReadings();
      closeReadingDialogs();
      refreshData();
      setError(null);
    } finally {
      setReadingSaving(false);
    }
  };

  const isDeleteScopeValid = React.useMemo(() => {
    if (deleteReadingsMode === 'before') {
      return Boolean(deleteReadingsBeforeDate);
    }
    if (deleteReadingsMode === 'between') {
      return Boolean(deleteReadingsStartDate)
        && Boolean(deleteReadingsEndDate)
        && deleteReadingsEndDate >= deleteReadingsStartDate;
    }
    return true;
  }, [deleteReadingsMode, deleteReadingsBeforeDate, deleteReadingsStartDate, deleteReadingsEndDate]);

  // Export filtered readings to CSV
  const handleExportCSV = async () => {
    if (!startDate || !endDate) return;

    const rawSerial = (device?.serialNumber || '').toString().trim();
    const safeSerial = rawSerial ? rawSerial.replace(/[^a-zA-Z0-9_-]+/g, '_') : `device_${deviceId}`;
    const fileName = `${safeSerial}_readings_${startDate}_${endDate}.csv`;

    const qs = new URLSearchParams();
    qs.append('startDate', startDate);
    qs.append('endDate', endDate);
    qs.append('order', 'asc');
    qs.append('format', 'csv');

    await downloadFile(`/devices/${deviceId}/readings?${qs.toString()}`, fileName);
  };

  // Chart data for daily usage
  const dailyUsageChartData = {
    labels: dailyUsage.slice(-30).map(item => item.date),
    datasets: [
      {
        label: `Daily Usage (${unit})`,
        data: dailyUsage.slice(-30).map(item => item.totalUsage),
        backgroundColor: colorPrimaryBg,
        borderColor: colorPrimaryBorder,
        borderWidth: 1,
      }
    ],
  };

  const monthlyUsageChartData = {
    labels: monthlyUsage.map(m => m.month),
    datasets: [
      {
        label: `Monthly Usage (${unit})`,
        data: monthlyUsage.map(m => m.total),
        backgroundColor: colorMonthlyBg,
        borderColor: colorMonthlyBorder,
        borderWidth: 1,
      }
    ],
  };

  // Chart data for daily reading counts (signal health)
  const dailyReadingCountChartData = {
    labels: dailyReadingCounts.slice(-30).map(item => item.date),
    datasets: [
      {
        label: 'Readings per Day',
        data: dailyReadingCounts.slice(-30).map(item => item.count),
        backgroundColor: 'rgba(255, 159, 64, 0.85)',
        borderColor: 'rgb(255, 159, 64)',
        borderWidth: 1,
      }
    ],
  };

  // Build category-based exact-time datasets without date adapter
  const formatLocalDate = (d) => {
    try {
      const dt = new Date(d);
      const y = dt.getFullYear();
      const m = String(dt.getMonth() + 1).padStart(2, '0');
      const da = String(dt.getDate()).padStart(2, '0');
      return `${y}-${m}-${da}`;
    } catch {
      return '';
    }
  };
  const todayDateStr = formatLocalDate(new Date());
  const yesterdayDateStr = formatLocalDate(new Date(Date.now() - 86400000));
  const todayReadings = Array.isArray(readings)
    ? readings.filter(r => r.readingTime && formatLocalDate(r.readingTime) === todayDateStr)
    : [];
  todayReadings.sort((a, b) => new Date(a.readingTime) - new Date(b.readingTime));
  const todayLabels = [];
  const todayValues = [];
  for (let i = 0; i < todayReadings.length; i++) {
    const curr = todayReadings[i];
    todayLabels.push(new Date(curr.readingTime).toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' }));
    todayValues.push(Math.max(0, Number(curr.usageSinceLastReading) || 0));
  }

  const yesterdayReadings = Array.isArray(readings)
    ? readings.filter(r => r.readingTime && formatLocalDate(r.readingTime) === yesterdayDateStr)
    : [];
  yesterdayReadings.sort((a, b) => new Date(a.readingTime) - new Date(b.readingTime));
  const yesterdayLabels = [];
  const yesterdayValues = [];
  for (let i = 0; i < yesterdayReadings.length; i++) {
    const curr = yesterdayReadings[i];
    yesterdayLabels.push(new Date(curr.readingTime).toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' }));
    yesterdayValues.push(Math.max(0, Number(curr.usageSinceLastReading) || 0));
  }

  const todayHourlyChartData = {
    labels: todayLabels,
    datasets: [
      {
        label: `Today's Usage (${unit})`,
        data: todayValues,
        borderColor: colorTodayBorder,
        backgroundColor: colorTodayBg,
        pointRadius: 3,
        showLine: true,
        tension: 0.2
      }
    ]
  };

  // Determine if there is any non-zero data for today's hourly usage
  const hasTodayHourlyData = Array.isArray(todayValues)
    ? todayValues.some(v => Number(v) > 0)
    : false;

  

  const yesterdayHourlyChartData = {
    labels: yesterdayLabels,
    datasets: [
      {
        label: `Yesterday's Usage (${unit})`,
        data: yesterdayValues,
        borderColor: colorYestBorder,
        backgroundColor: colorYestBg,
        pointRadius: 3,
        showLine: true,
        tension: 0.2
      }
    ]
  };

  // Chart options for responsive charts that match Site overview
  const chartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    devicePixelRatio: devicePixelRatio || 1,
    animation: false,
    layout: {
      padding: { top: isMobile ? 10 : 16, right: 0, bottom: isMobile ? 2 : 4, left: 0 }
    },
    color: chartTextColor,
    plugins: {
      legend: {
        display: !isMobile,
        position: 'top',
        labels: {
          color: chartTextColor,
          boxWidth: 12,
          padding: 10,
          font: {
            size: isMobile ? 11 : 12
          }
        }
      },
      datalabels: {
        display: false
      },
      barValueLabels: { display: false },
      barValueLabelsV2: {
        display: false
      },
      barValueLabelsV3: false,
      barValueLabelsV4: {
        display: !isMobile,
        color: chartTextColor,
        insideColor: chartTextColor,
        fontSize: isMobile ? 9 : 10,
        offset: 6,
        minValue: 0,
        position: 'top',
        showOnlyExtremesOnMobile: true,
        isMobile,
        denseDataThreshold: 10,
        formatter: (v) => {
          const num = Number(v);
          if (!Number.isFinite(num)) return '';
          const abs = Math.abs(num);
          const decimals = isElectrical ? (abs >= 100 ? 0 : abs >= 10 ? 1 : 2) : 0;
          return num.toLocaleString('en-US', { maximumFractionDigits: decimals });
        }
      },
      tooltip: {
        backgroundColor: chartTooltipBgColor,
        titleColor: chartTooltipTextColor,
        bodyColor: chartTooltipTextColor,
        footerColor: chartTooltipTextColor,
        borderColor: chartTooltipBorderColor,
        borderWidth: 1,
        cornerRadius: 6,
        callbacks: {
          title: function (context) {
            // Format the date for better readability
            const dateStr = context[0].label;
            try {
              if (dateStr.includes(':00')) {
                return dateStr; // It's an hour label
              }
              const date = new Date(dateStr);
              if (isNaN(date.getTime())) {
                return dateStr; // Return original if invalid
              }
              return date.toLocaleDateString('en-US', {
                weekday: 'short',
                year: 'numeric',
                month: 'short',
                day: 'numeric'
              });
            } catch (error) {
              console.error('Error formatting tooltip date:', error);
              return dateStr;
            }
          },
          label: function (context) {
            // Format the value with proper units
            return `Usage: ${formatNumber(context.raw)} ${unit}`;
          }
        }
      }
    },
    scales: {
      y: {
        beginAtZero: true,
        title: {
          display: !isMobile,
          text: `Usage (${unit})`,
          color: chartTextColor
        },
        ticks: {
          color: chartTextColor,
          font: {
            size: isMobile ? 9 : 10
          }
        },
        grid: {
          drawBorder: false,
          color: chartGridColor
        },
        border: {
          color: chartGridColor
        }
      },
      x: {
        ticks: {
          color: chartTextColor,
          maxRotation: isMobile ? 0 : 45,
          minRotation: isMobile ? 0 : 45,
          autoSkip: true,
          maxTicksLimit: isMobile ? 6 : 10,
          font: { size: isMobile ? 9 : 10 }
        },
        grid: {
          display: false,
          color: chartGridColor
        },
        border: {
          color: chartGridColor
        }
      }
    }
  };

  

  // Tab panel content for device overview
  if (loading) {
    return (
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          height: '50vh'
        }}
      >
        <CircularProgress />
      </Box>
    );
  }

  if (error && !device) {
    return (
      <Box sx={{ textAlign: 'center', mt: 4, px: { xs: 1, sm: 2 } }}>
        <Typography variant="h5" gutterBottom>
          {error}
        </Typography>
        <Button
          variant="contained"
          startIcon={<ArrowBack />}
          onClick={handleBackClick}
          sx={{ mt: 2 }}
        >
          {backButtonLabel}
        </Button>
      </Box>
    );
  }

  return (
    <Box sx={{ p: { xs: 1, sm: 2, md: 3 } }}>
      <Box sx={{ mb: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 1, mb: { xs: 2, sm: 0 } }}>
          <Button
            variant="outlined"
            startIcon={<ArrowBack />}
            onClick={handleBackClick}
            size={isMobile ? 'small' : 'medium'}
            sx={{ mr: 2 }}
          >
            {backButtonLabel}
          </Button>
          <Typography
            variant={isMobile ? 'h5' : 'h4'}
            component="h1"
            sx={{
              flexGrow: 1,
              wordBreak: 'break-word',
              mt: { xs: 1, sm: 0 }
            }}
          >
            {device?.description || device?.serialNumber || 'Device Details'}
          </Typography>
        </Box>
      </Box>

      <Grid container spacing={2}>
        <Grid item xs={12}>
          <Card variant="outlined">
            <CardHeader
              title="Device Overview"
              titleTypographyProps={{ variant: isMobile ? 'h6' : 'h5' }}
              action={
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                  <Tooltip title={isEndUser ? 'Edit Threshold' : 'Edit Device'}>
                    <IconButton size="small" onClick={handleEditDevice}>
                      <Edit fontSize="small" />
                    </IconButton>
                  </Tooltip>
                  <Tooltip title="Delete Device">
                    <span>
                      <IconButton
                        size="small"
                        color="error"
                        disabled={!isAdmin}
                        onClick={handleDeleteDialogOpen}
                      >
                        <Delete fontSize="small" />
                      </IconButton>
                    </span>
                  </Tooltip>
                </Box>
              }
              sx={{ pb: isMobile ? 0.5 : 1 }}
            />
            <Divider />
            <CardContent sx={{ p: { xs: 0.75, sm: 1.5 } }}>
              <Grid container spacing={isMobile ? 0.75 : 1}>
                <Grid item xs={6} sm={4} md={4}>
                  <Box sx={{ display: 'flex', alignItems: 'center' }}>
                    <DeviceHub sx={{ mr: 0.5, color: theme.palette.primary.main, fontSize: iconSize }} />
                    <Box sx={{ overflow: 'hidden', minWidth: 0 }}>
                      <Typography variant="caption" color="text.secondary" noWrap>
                        Serial Number
                      </Typography>
                      <Typography variant="body2" noWrap sx={{ fontWeight: 500 }}>
                        {device?.serialNumber || 'N/A'}
                      </Typography>
                    </Box>
                  </Box>
                </Grid>

                <Grid item xs={6} sm={4} md={4}>
                  <Box sx={{ display: 'flex', alignItems: 'center' }}>
                    <Business sx={{ mr: 0.5, color: theme.palette.primary.main, fontSize: iconSize }} />
                    <Box sx={{ overflow: 'hidden', minWidth: 0 }}>
                      <Typography variant="caption" color="text.secondary" noWrap>
                        Site
                      </Typography>
                      <Typography variant="body2" noWrap sx={{ fontWeight: 500 }}>
                        {Array.isArray(device?.sites) && device.sites.length > 0
                          ? device.sites.map((s) => s.description).filter(Boolean).join(', ')
                          : 'Not assigned'}
                      </Typography>
                    </Box>
                  </Box>
                </Grid>

                <Grid item xs={12} sm={4} md={4}>
                  <Box sx={{ display: 'flex', alignItems: 'center' }}>
                    <LocationOn sx={{ mr: 0.5, color: theme.palette.primary.main, fontSize: iconSize }} />
                    <Box sx={{ overflow: 'hidden', minWidth: 0 }}>
                      <Typography variant="caption" color="text.secondary" noWrap>
                        Location
                      </Typography>
                      <Typography
                        variant="body2"
                        noWrap={!isMobile}
                        sx={{
                          fontWeight: 500,
                          ...(isMobile
                            ? {
                                display: '-webkit-box',
                                WebkitLineClamp: 2,
                                WebkitBoxOrient: 'vertical',
                                overflow: 'hidden'
                              }
                            : {})
                        }}
                      >
                        {device?.location || '-'}
                      </Typography>
                    </Box>
                  </Box>
                </Grid>

                <Grid item xs={6} sm={4} md={4}>
                  <Box sx={{ display: 'flex', alignItems: 'center' }}>
                    {device?.deviceType === 'electrical_meter' ? (
                      <Bolt sx={{ mr: 0.5, color: theme.palette.warning.main, fontSize: iconSize }} />
                    ) : (
                      <WaterDrop sx={{ mr: 0.5, color: theme.palette.primary.main, fontSize: iconSize }} />
                    )}
                    <Box sx={{ overflow: 'hidden', minWidth: 0 }}>
                      <Typography variant="caption" color="text.secondary" noWrap>
                        Type
                      </Typography>
                      <Typography variant="body2" noWrap sx={{ fontWeight: 500 }}>
                        {device?.deviceType === 'electrical_meter' ? 'Electrical meter' : 'Water meter'}
                      </Typography>
                    </Box>
                  </Box>
                </Grid>

                <Grid item xs={6} sm={4} md={4}>
                  <Box sx={{ display: 'flex', alignItems: 'center' }}>
                    {device?.isActive ? (
                      <CheckCircle sx={{ mr: 0.5, color: theme.palette.success.main, fontSize: iconSize }} />
                    ) : (
                      <Warning sx={{ mr: 0.5, color: theme.palette.text.secondary, fontSize: iconSize }} />
                    )}
                    <Box sx={{ overflow: 'hidden', minWidth: 0 }}>
                      <Typography variant="caption" color="text.secondary" noWrap>
                        Status
                      </Typography>
                      <Typography variant="body2" noWrap sx={{ fontWeight: 500 }}>
                        {device?.isActive ? 'Active' : 'Inactive'}
                      </Typography>
                    </Box>
                  </Box>
                </Grid>

                <Grid item xs={6} sm={4} md={4}>
                  <Box sx={{ display: 'flex', alignItems: 'center' }}>
                    <AccessTime sx={{ mr: 0.5, color: theme.palette.primary.main, fontSize: iconSize }} />
                    <Box sx={{ overflow: 'hidden', minWidth: 0 }}>
                      <Typography variant="caption" color="text.secondary" noWrap>
                        Updated
                      </Typography>
                      <Typography variant="body2" noWrap sx={{ fontWeight: 500 }}>
                        {device?.lastReading ? formatDate(device.lastReading) : 'N/A'}
                      </Typography>
                    </Box>
                  </Box>
                </Grid>

                <Grid item xs={6} sm={4} md={4}>
                  <Box sx={{ display: 'flex', alignItems: 'center' }}>
                    <Settings sx={{ mr: 0.5, color: theme.palette.primary.main, fontSize: iconSize }} />
                    <Box sx={{ overflow: 'hidden', minWidth: 0 }}>
                      <Typography variant="caption" color="text.secondary" noWrap>
                        Multiplier
                      </Typography>
                      <Typography variant="body2" noWrap sx={{ fontWeight: 500 }}>
                        {device?.multiplier !== undefined && device?.multiplier !== null ? Number(device.multiplier) : 1}
                      </Typography>
                    </Box>
                  </Box>
                </Grid>

              </Grid>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      <Box sx={{ mt: { xs: 2, sm: 3 } }}>
      <Card elevation={2}>
        <Tabs
          value={tabValue}
          onChange={handleTabChange}
          aria-label="device tabs"
          variant="scrollable"
          scrollButtons="auto"
          allowScrollButtonsMobile
          sx={{ borderBottom: 1, borderColor: 'divider', px: 2 }}
        >
          <Tab label="Overview" {...a11yProps(0)} />
          <Tab label="Readings" {...a11yProps(1)} />
          <Tab label="Notifications" {...a11yProps(2)} />
        </Tabs>
        
        {/* Overview Tab */}
        <TabPanel value={tabValue} index={0}>
          <Grid container spacing={{ xs: 1, sm: 2 }}>
            {hasTodayHourlyData && (
              <Grid item xs={12} md={6}>
                <Card variant="outlined">
                  <CardHeader 
                    title="Today's Usage (Exact Time)" 
                    titleTypographyProps={{ variant: 'subtitle1', fontWeight: 600 }} 
                    sx={{ py: 1 }} 
                  />
                  <Divider />
                  <CardContent sx={{ 
                    height: isMobile ? 220 : 250, 
                    p: 1,
                    width: '100%',
                    '& .chartjs-size-monitor': {
                      overflow: 'hidden',
                      position: 'relative',
                      width: '100%',
                      height: '100%',
                      '& > div': {
                        position: 'absolute',
                        top: 0,
                        left: 0,
                        right: 0,
                        bottom: 0,
                        overflow: 'hidden',
                        pointerEvents: 'none',
                        visibility: 'hidden',
                        zIndex: -1,
                      }
                    }
                  }}>
                    <Bar
                      key={`today-${deviceId}-dpr-${devicePixelRatio}-mode-${theme.palette.mode}`}
                      ref={todayHourlyChartRef}
                      options={chartOptions}
                      data={todayHourlyChartData}
                      redraw
                    />
                  </CardContent>
                </Card>
              </Grid>
            )}
            {hasTodayHourlyData ? (
              <Grid item xs={12} md={6}>
                <Card variant="outlined">
                  <CardHeader 
                    title="Yesterday's Usage (Exact Time)" 
                    titleTypographyProps={{ variant: 'subtitle1', fontWeight: 600 }} 
                    sx={{ py: 1 }} 
                  />
                  <Divider />
                  <CardContent sx={{ 
                    height: isMobile ? 220 : 250, 
                    p: 1,
                    '& .chartjs-size-monitor': {
                      overflow: 'hidden',
                      position: 'relative',
                      width: '100%',
                      height: '100%',
                      '& > div': {
                        position: 'absolute',
                        top: 0,
                        left: 0,
                        right: 0,
                        bottom: 0,
                        overflow: 'hidden',
                        pointerEvents: 'none',
                        visibility: 'hidden',
                        zIndex: -1,
                      }
                    }
                  }}>
                    <Bar
                      key={`yesterday-${deviceId}-dpr-${devicePixelRatio}-mode-${theme.palette.mode}`}
                      ref={yesterdayHourlyChartRef}
                      options={chartOptions}
                      data={yesterdayHourlyChartData}
                      redraw
                    />
                  </CardContent>
                </Card>
              </Grid>
            ) : (
              <Grid item xs={12} md={12}>
                <Card variant="outlined" sx={{ width: '100%', mx: { xs: 0, sm: 0 } }}>
                  <CardHeader 
                    title="Yesterday's Usage (Exact Time)" 
                    titleTypographyProps={{ variant: 'subtitle1', fontWeight: 600 }} 
                    sx={{ py: 1 }} 
                  />
                  <Divider />
                  <CardContent sx={{ height: isMobile ? 220 : 250, p: 1, width: '100%' }}>
                    <Bar
                      key={`yesterday-full-${deviceId}-dpr-${devicePixelRatio}-mode-${theme.palette.mode}`}
                      ref={yesterdayHourlyChartRef}
                      options={chartOptions}
                      data={yesterdayHourlyChartData}
                      redraw
                    />
                  </CardContent>
                </Card>
              </Grid>
            )}
            <Grid item xs={12}>
              <Card variant="outlined">
                <CardHeader 
                  title="Daily Usage (Last 30 Days)" 
                  subheader={`Daily usage includes multiplier effective on each day (current multiplier: x${Number(device?.multiplier) || 1}).`}
                  subheaderTypographyProps={{ variant: 'caption' }}
                  titleTypographyProps={{ variant: 'subtitle1', fontWeight: 600 }} 
                  sx={{ py: 1 }} 
                />
                <Divider />
                <CardContent sx={{ 
                  height: isMobile ? 220 : 250, 
                  p: 1,
                  '& .chartjs-size-monitor': {
                    overflow: 'hidden',
                    position: 'relative',
                    width: '100%',
                    height: '100%',
                    '& > div': {
                      position: 'absolute',
                      top: 0,
                      left: 0,
                      right: 0,
                      bottom: 0,
                      overflow: 'hidden',
                      pointerEvents: 'none',
                      visibility: 'hidden',
                      zIndex: -1,
                    }
                  }
                }}>
                  <Bar
                    key={`daily-${deviceId}-dpr-${devicePixelRatio}-mode-${theme.palette.mode}`}
                    ref={dailyChartRef}
                    options={chartOptions}
                    data={dailyUsageChartData}
                  />
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={12}>
              <Card variant="outlined">
                <CardHeader 
                  title="Monthly Usage (Last 12 Months)" 
                  titleTypographyProps={{ variant: 'subtitle1', fontWeight: 600 }} 
                  sx={{ py: 1 }} 
                />
                <Divider />
                <CardContent sx={{ 
                  height: isMobile ? 220 : 250, 
                  p: 1,
                  '& .chartjs-size-monitor': {
                    overflow: 'hidden',
                    position: 'relative',
                    width: '100%',
                    height: '100%',
                    '& > div': {
                      position: 'absolute',
                      top: 0,
                      left: 0,
                      right: 0,
                      bottom: 0,
                      overflow: 'hidden',
                      pointerEvents: 'none',
                      visibility: 'hidden',
                      zIndex: -1,
                    }
                  }
                }}>
                  <Bar
                    key={`monthly-${deviceId}-dpr-${devicePixelRatio}-mode-${theme.palette.mode}`}
                    options={chartOptions}
                    data={monthlyUsageChartData}
                  />
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={12}>
              <Card variant="outlined">
                <CardHeader 
                  title="Daily Reading Count (Last 30 Days)" 
                  subheader="Higher counts generally indicate better signal health"
                  titleTypographyProps={{ variant: 'subtitle1', fontWeight: 600 }} 
                  sx={{ py: 1 }} 
                />
                <Divider />
                <CardContent sx={{ 
                  height: isMobile ? 220 : 250, 
                  p: 1,
                  '& .chartjs-size-monitor': {
                    overflow: 'hidden',
                    position: 'relative',
                    width: '100%',
                    height: '100%',
                    '& > div': {
                      position: 'absolute',
                      top: 0,
                      left: 0,
                      right: 0,
                      bottom: 0,
                      overflow: 'hidden',
                      pointerEvents: 'none',
                      visibility: 'hidden',
                      zIndex: -1,
                    }
                  }
                }}>
                  <Bar
                    key={`reading-count-${deviceId}-dpr-${devicePixelRatio}-mode-${theme.palette.mode}`}
                    options={{
                      ...chartOptions,
                      scales: {
                        ...chartOptions.scales,
                        y: {
                          ...chartOptions.scales.y,
                          title: { display: true, text: 'Readings (count)' }
                        }
                      },
                      plugins: {
                        ...chartOptions.plugins,
                        tooltip: {
                          ...chartOptions.plugins.tooltip,
                          callbacks: {
                            ...chartOptions.plugins.tooltip.callbacks,
                            label: function (context) {
                              return `Count: ${context.raw}`;
                            }
                          }
                        }
                      }
                    }}
                    data={dailyReadingCountChartData}
                  />
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={12}>
              <Card variant="outlined">
                <CardHeader 
                  title="Recent Readings" 
                  subheader={`Reading values are raw. Usage includes multiplier (current multiplier: x${Number(device?.multiplier) || 1}).`}
                  subheaderTypographyProps={{ variant: 'caption' }}
                  titleTypographyProps={{ variant: 'subtitle1', fontWeight: 600 }} 
                  sx={{ py: 1 }} 
                />
                <Divider />
                <CardContent sx={{ p: 1 }}>
                  {readings.length > 0 ? (
                    <TableContainer>
                      <Table size="small">
                        <TableHead>
                          <TableRow>
                            <TableCell>Date & Time</TableCell>
                            <TableCell>Reading Value (Raw)</TableCell>
                            <TableCell>{`Usage Since Last (${unit}, Multiplier Applied)`}</TableCell>
                            <TableCell>Status</TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {readings.slice(0, 5).map((reading) => (
                            <TableRow key={reading.id}>
                              <TableCell>{formatDate(reading.readingTime)}</TableCell>
                              <TableCell>{formatNumber(reading.reading)}</TableCell>
                              <TableCell>
                                {formatNumber(reading.usageSinceLastReading)} {unit}
                                <Typography component="span" variant="caption" color="text.secondary" sx={{ ml: 0.75 }}>
                                  (x{Number(reading?.appliedMultiplier) || 1})
                                </Typography>
                              </TableCell>
                              <TableCell>
                                <Chip
                                  label={reading.status || 'Normal'}
                                  color={reading.status === 'Error' ? 'error' : 'success'}
                                  size="small"
                                />
                              </TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </TableContainer>
                  ) : (
                    <Typography variant="body1" color="text.secondary" sx={{ p: 2 }}>
                      No readings available for this device.
                    </Typography>
                  )}
                </CardContent>
              </Card>
            </Grid>
          </Grid>
        </TabPanel>

        <Dialog
          open={readingEditOpen}
          onClose={closeReadingDialogs}
          fullScreen={isMobile}
          scroll="paper"
          maxWidth="xs"
          fullWidth
          PaperProps={{
            sx: {
              width: '100%',
              maxWidth: { xs: '100%', sm: 450 },
              m: { xs: 0, sm: 2 },
              maxHeight: { xs: '100dvh', sm: 'calc(100vh - 64px)' }
            }
          }}
        >
          <DialogTitle>Edit Reading</DialogTitle>
          <DialogContent>
            <Grid container spacing={2} sx={{ mt: 0.5 }}>
              <Grid item xs={12}>
                <TextField
                  label="Reading Value"
                  type="number"
                  fullWidth
                  value={editReadingValue}
                  onChange={(e) => setEditReadingValue(e.target.value)}
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  label="Reading Time"
                  type="datetime-local"
                  fullWidth
                  value={editReadingTime}
                  onChange={(e) => setEditReadingTime(e.target.value)}
                  InputLabelProps={{ shrink: true }}
                />
              </Grid>
            </Grid>
          </DialogContent>
          <DialogActions>
            <Button onClick={closeReadingDialogs} disabled={readingSaving}>Cancel</Button>
            <Button variant="contained" onClick={handleSaveReadingEdit} disabled={readingSaving}>
              {readingSaving ? 'Saving...' : 'Save'}
            </Button>
          </DialogActions>
        </Dialog>

        <Dialog
          open={readingDeleteOpen}
          onClose={closeReadingDialogs}
          fullScreen={isMobile}
          scroll="paper"
          maxWidth="xs"
          fullWidth
          PaperProps={{
            sx: {
              width: '100%',
              maxWidth: { xs: '100%', sm: 450 },
              m: { xs: 0, sm: 2 },
              maxHeight: { xs: '100dvh', sm: 'calc(100vh - 64px)' }
            }
          }}
        >
          <DialogTitle>Delete Reading</DialogTitle>
          <DialogContent>
            <DialogContentText>
              Are you sure you want to delete this reading? This cannot be undone.
            </DialogContentText>
          </DialogContent>
          <DialogActions>
            <Button onClick={closeReadingDialogs} disabled={readingSaving}>Cancel</Button>
            <Button color="error" variant="contained" onClick={handleConfirmReadingDelete} disabled={readingSaving}>
              {readingSaving ? 'Deleting...' : 'Delete'}
            </Button>
          </DialogActions>
        </Dialog>

        <Dialog
          open={readingDeleteAllOpen}
          onClose={closeReadingDialogs}
          fullScreen={isMobile}
          scroll="paper"
          maxWidth="sm"
          fullWidth
          PaperProps={{
            sx: {
              width: '100%',
              maxWidth: { xs: '100%', sm: 600 },
              m: { xs: 0, sm: 2 },
              maxHeight: { xs: '100dvh', sm: 'calc(100vh - 64px)' }
            }
          }}
        >
          <DialogTitle>Delete Device Readings</DialogTitle>
          <DialogContent>
            <DialogContentText sx={{ mb: 2 }}>
              Choose which readings to delete for this device. This action cannot be undone.
            </DialogContentText>
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <FormControl fullWidth size="small">
                  <InputLabel id="delete-readings-mode-label">Delete Scope</InputLabel>
                  <Select
                    labelId="delete-readings-mode-label"
                    label="Delete Scope"
                    value={deleteReadingsMode}
                    onChange={(e) => setDeleteReadingsMode(e.target.value)}
                  >
                    <MenuItem value="all">All readings</MenuItem>
                    <MenuItem value="before">All readings before a date</MenuItem>
                    <MenuItem value="between">Readings between two dates</MenuItem>
                  </Select>
                </FormControl>
              </Grid>

              {deleteReadingsMode === 'before' && (
                <Grid item xs={12}>
                  <TextField
                    label="Delete Before"
                    type="date"
                    value={deleteReadingsBeforeDate}
                    onChange={(e) => setDeleteReadingsBeforeDate(e.target.value)}
                    fullWidth
                    size="small"
                    InputLabelProps={{ shrink: true }}
                    helperText="Deletes readings earlier than this date."
                  />
                </Grid>
              )}

              {deleteReadingsMode === 'between' && (
                <>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      label="Start Date"
                      type="date"
                      value={deleteReadingsStartDate}
                      onChange={(e) => setDeleteReadingsStartDate(e.target.value)}
                      fullWidth
                      size="small"
                      InputLabelProps={{ shrink: true }}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      label="End Date"
                      type="date"
                      value={deleteReadingsEndDate}
                      onChange={(e) => setDeleteReadingsEndDate(e.target.value)}
                      fullWidth
                      size="small"
                      InputLabelProps={{ shrink: true }}
                    />
                  </Grid>
                </>
              )}

              {!isDeleteScopeValid && (
                <Grid item xs={12}>
                  <Alert severity="warning">
                    Please provide a valid date selection before deleting.
                  </Alert>
                </Grid>
              )}
            </Grid>
          </DialogContent>
          <DialogActions>
            <Button onClick={closeReadingDialogs} disabled={readingSaving}>Cancel</Button>
            <Button
              color="error"
              variant="contained"
              onClick={handleConfirmDeleteAllReadings}
              disabled={readingSaving || !isDeleteScopeValid}
            >
              {readingSaving ? 'Deleting...' : 'Delete Readings'}
            </Button>
          </DialogActions>
        </Dialog>
        
        {/* Readings Tab */}
        <TabPanel value={tabValue} index={1}>
          <Box>
            <Alert severity="info" sx={{ mb: 2 }}>
              Reading values are raw (natural). Usage values include the multiplier applied for each reading date.
            </Alert>
            <Grid container spacing={2} alignItems="center" sx={{ mb: 2 }}>
              <Grid item xs={12} sm={4}>
                <TextField
                  label="From"
                  type="date"
                  value={startDate}
                  onChange={(e) => { setStartDate(e.target.value); setReadingsPage(0); }}
                  fullWidth
                  variant="outlined"
                  size="small"
                  InputLabelProps={{ shrink: true }}
                />
              </Grid>
              <Grid item xs={12} sm={4}>
                <TextField
                  label="To"
                  type="date"
                  value={endDate}
                  onChange={(e) => { setEndDate(e.target.value); setReadingsPage(0); }}
                  fullWidth
                  variant="outlined"
                  size="small"
                  InputLabelProps={{ shrink: true }}
                />
              </Grid>
              <Grid item xs={12} sm={4}>
                <Button
                  variant="contained"
                  onClick={handleExportCSV}
                  disabled={!startDate || !endDate}
                  fullWidth
                >
                  Export CSV
                </Button>
              </Grid>
              {canDeleteReadings && (
                <Grid item xs={12}>
                  <Button
                    variant="outlined"
                    color="error"
                    onClick={openDeleteAllReadings}
                    disabled={totalReadings === 0}
                  >
                    Delete Readings For This Device
                  </Button>
                </Grid>
              )}
            </Grid>
            {paginatedReadingsLoading ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                <CircularProgress size={32} />
              </Box>
            ) : paginatedReadings.length > 0 ? (
              <TableContainer component={Paper} elevation={0} variant="outlined">
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell>Date & Time</TableCell>
                      <TableCell>Reading Value (Raw)</TableCell>
                      <TableCell>{`Usage Since Last (${unit}, Multiplier Applied)`}</TableCell>
                      <TableCell>Status</TableCell>
                      {canManageReadings && <TableCell align="right">Actions</TableCell>}
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {paginatedReadings.map((reading) => (
                      <TableRow key={reading.id}>
                        <TableCell>{formatDate(reading.readingTime)}</TableCell>
                        <TableCell>{formatNumber(reading.reading)}</TableCell>
                        <TableCell>
                          {formatNumber(reading.usageSinceLastReading)} {unit}
                          <Typography component="span" variant="caption" color="text.secondary" sx={{ ml: 0.75 }}>
                            (x{Number(reading?.appliedMultiplier) || 1})
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Chip
                            label={reading.status || 'Normal'}
                            color={reading.status === 'Error' ? 'error' : 'success'}
                            size="small"
                          />
                        </TableCell>
                        {canManageReadings && (
                          <TableCell align="right">
                            <Tooltip title="Edit reading">
                              <span>
                                <IconButton size="small" onClick={() => openEditReading(reading)}>
                                  <Edit fontSize="inherit" />
                                </IconButton>
                              </span>
                            </Tooltip>
                            {canDeleteReadings && (
                              <Tooltip title="Delete reading">
                                <span>
                                  <IconButton size="small" color="error" onClick={() => openDeleteReading(reading)}>
                                    <Delete fontSize="inherit" />
                                  </IconButton>
                                </span>
                              </Tooltip>
                            )}
                          </TableCell>
                        )}
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
                <TablePagination
                  component="div"
                  count={totalReadings}
                  page={readingsPage}
                  onPageChange={(e, newPage) => setReadingsPage(newPage)}
                  rowsPerPage={readingsRowsPerPage}
                  onRowsPerPageChange={(e) => {
                    setReadingsRowsPerPage(parseInt(e.target.value, 10));
                    setReadingsPage(0);
                  }}
                  rowsPerPageOptions={[10, 25, 50, 100]}
                />
              </TableContainer>
            ) : (
              <Alert severity="info" sx={{ mt: 2 }}>
                No readings available for this device in the selected date range.
              </Alert>
            )}
          </Box>
        </TabPanel>
        
        {/* Notifications Tab */}
        <TabPanel value={tabValue} index={2}>
          <Box sx={{ p: 1 }}>
            <Typography variant="h6" gutterBottom>
              Notifications
            </Typography>
            {device?.notifications && device.notifications.length > 0 ? (
              <TableContainer component={Paper} elevation={0} variant="outlined">
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Date</TableCell>
                      <TableCell>Type</TableCell>
                      <TableCell>Message</TableCell>
                      <TableCell>Status</TableCell>
                      <TableCell>Resolved At</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {[...device.notifications]
                      .sort((a, b) => new Date(b.detectedAt) - new Date(a.detectedAt))
                      .map((n) => (
                      <TableRow key={n.id} hover>
                        <TableCell>{formatDate(n.detectedAt)}</TableCell>
                        <TableCell>
                          <Chip label={n.type} color="warning" size="small" />
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2">{n.message}</Typography>
                        </TableCell>
                        <TableCell>
                          <Chip 
                            label={(n.status || 'new').toUpperCase()} 
                            color={n.status === 'resolved' ? 'success' : (n.status === 'viewed' ? 'warning' : 'error')} 
                            size="small"
                          />
                        </TableCell>
                        <TableCell>{n.resolvedAt ? formatDate(n.resolvedAt) : '-'}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            ) : (
              <Alert severity="info" sx={{ mt: 2 }}>
                No notifications for this device yet.
              </Alert>
            )}
          </Box>
        </TabPanel>

        <Dialog
          open={editDialogOpen}
          onClose={handleCloseEditDialog}
          fullScreen={isMobile}
          scroll="paper"
          maxWidth={isMobile ? 'xs' : 'sm'}
          fullWidth
          PaperProps={{
            sx: {
              width: '100%',
              maxWidth: { xs: '100%', sm: 600 },
              m: { xs: 0, sm: 2 },
              maxHeight: { xs: '100dvh', sm: 'calc(100vh - 64px)' }
            }
          }}
        >
          <DialogTitle>
            {isEndUser ? 'Edit Threshold' : 'Edit Device'}
          </DialogTitle>
          <DialogContent>
            <Grid container spacing={2} sx={{ mt: 1 }}>
              {isEndUser ? (
                <Grid item xs={12}>
                  <TextField
                    name="dailyThresholdLiters"
                    label={`Daily Threshold (${deviceFormData.deviceType === 'electrical_meter' ? 'kWh' : 'L'})`}
                    type="number"
                    inputProps={{ step: 0.1, min: 0 }}
                    fullWidth
                    value={deviceFormData.dailyThresholdLiters}
                    onChange={handleDeviceFormChange}
                    helperText="If set, exceeding this daily usage will create a notification"
                  />
                </Grid>
              ) : (
                <>
                  <Grid item xs={12}>
                    <TextField
                      name="serialNumber"
                      label="Serial Number"
                      fullWidth
                      required
                      disabled={!isAdminOrOperator}
                      value={deviceFormData.serialNumber}
                      onChange={handleDeviceFormChange}
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <TextField
                      name="accountNumber"
                      label="Account Number"
                      fullWidth
                      value={deviceFormData.accountNumber}
                      onChange={handleDeviceFormChange}
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <TextField
                      name="description"
                      label="Description"
                      fullWidth
                      value={deviceFormData.description}
                      onChange={handleDeviceFormChange}
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <StructuredLocationFields
                      value={deviceFormData.location}
                      onChange={(nextLocation) => setDeviceFormData((p) => ({ ...p, location: nextLocation }))}
                      title="Location"
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <Grid container spacing={2}>
                      <Grid item xs={12} sm={6}>
                        <TextField
                          name="latitude"
                          label="Latitude (Optional)"
                          type="number"
                          inputProps={{ step: 0.0000001, min: -90, max: 90 }}
                          fullWidth
                          value={deviceFormData.latitude}
                          onChange={handleDeviceFormChange}
                        />
                      </Grid>
                      <Grid item xs={12} sm={6}>
                        <TextField
                          name="longitude"
                          label="Longitude (Optional)"
                          type="number"
                          inputProps={{ step: 0.0000001, min: -180, max: 180 }}
                          fullWidth
                          value={deviceFormData.longitude}
                          onChange={handleDeviceFormChange}
                        />
                      </Grid>
                      <Grid item xs={12}>
                        <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
                          <Tooltip title="Use current GPS">
                            <span>
                              <IconButton onClick={handleUseCurrentGps} disabled={gpsLocating}>
                                {gpsLocating ? <CircularProgress size={20} /> : <MyLocation />}
                              </IconButton>
                            </span>
                          </Tooltip>
                        </Box>
                      </Grid>
                    </Grid>
                  </Grid>
                  <Grid item xs={12}>
                    <TextField
                      name="multiplier"
                      label="Multiplier"
                      type="number"
                      inputProps={{ step: 0.0001, min: 0 }}
                      fullWidth
                      disabled={!isAdminOrOperator}
                      value={deviceFormData.multiplier}
                      onChange={handleDeviceFormChange}
                      helperText="Multiply raw readings by this value (e.g., 1000 for x1000)"
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <TextField
                      name="highUsageThresholdLiters"
                      label={`High Usage Threshold (${deviceFormData.deviceType === 'electrical_meter' ? 'kWh' : 'L'})`}
                      type="number"
                      inputProps={{ step: 0.1, min: 0 }}
                      fullWidth
                      value={deviceFormData.highUsageThresholdLiters}
                      onChange={handleDeviceFormChange}
                      helperText="If a single reading exceeds this usage, a high usage notification may be created"
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <TextField
                      name="dailyThresholdLiters"
                      label={`Daily Threshold (${deviceFormData.deviceType === 'electrical_meter' ? 'kWh' : 'L'})`}
                      type="number"
                      inputProps={{ step: 0.1, min: 0 }}
                      fullWidth
                      value={deviceFormData.dailyThresholdLiters}
                      onChange={handleDeviceFormChange}
                      helperText="If set, exceeding this daily usage will create a notification"
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <TextField
                      name="decryptKey"
                      label="Decryption Key (Kamstrup Devices)"
                      fullWidth
                      disabled={!isAdminOrOperator}
                      value={deviceFormData.decryptKey}
                      onChange={handleDeviceFormChange}
                      helperText="32-character hex key for Kamstrup Advanced meters (leave empty if not applicable)"
                      placeholder="e.g., 0123456789ABCDEF0123456789ABCDEF"
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <Autocomplete
                      multiple
                      disabled={!isAdminOrOperator}
                      options={sites}
                      getOptionLabel={(option) => option.description || ''}
                      isOptionEqualToValue={(option, value) => String(option.id) === String(value.id)}
                      value={sites.filter((s) => (deviceFormData.siteIds || []).map(String).includes(String(s.id)))}
                      onChange={handleSiteSelection}
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          label="Sites"
                          placeholder="Select one or more sites"
                          fullWidth
                          helperText="A meter can be assigned to more than one site (e.g. bulk on one, sub-meter on another)"
                        />
                      )}
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <Autocomplete
                      disabled={!isAdminOrOperator}
                      options={parentDeviceOptions.filter((d) => String(d.id) !== String(deviceId || 'new'))}
                      getOptionLabel={(option) => {
                        const serial = option?.serialNumber ? String(option.serialNumber) : '';
                        const desc = option?.description ? String(option.description) : '';
                        const siteDesc = option?.sites?.[0]?.description ? String(option.sites[0].description) : '';
                        const main = serial && desc ? `${serial} - ${desc}` : (desc || serial);
                        return siteDesc ? `${main} (${siteDesc})` : main;
                      }}
                      value={parentDeviceOptions.find((d) => String(d.id) === String(deviceFormData.parentId)) || null}
                      isOptionEqualToValue={(option, value) => String(option.id) === String(value.id)}
                      onChange={handleParentDeviceSelection}
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          label="Parent Device (Optional)"
                          fullWidth
                          helperText="Select a parent device to create a hierarchy"
                          disabled={parentDeviceOptionsLoading}
                        />
                      )}
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <FormControl fullWidth disabled={!isAdminOrOperator}>
                      <InputLabel id="device-type-label">Device Type</InputLabel>
                      <Select
                        labelId="device-type-label"
                        name="deviceType"
                        value={deviceFormData.deviceType}
                        label="Device Type"
                        onChange={handleDeviceFormChange}
                      >
                        <MenuItem value="water_meter">Water Meter</MenuItem>
                        <MenuItem value="electrical_meter">Electrical Meter</MenuItem>
                      </Select>
                    </FormControl>
                  </Grid>
                  <Grid item xs={12}>
                    <FormControlLabel
                      control={
                        <Switch
                          checked={deviceFormData.isActive}
                          disabled={!isAdminOrOperator}
                          onChange={(e) => setDeviceFormData({
                            ...deviceFormData,
                            isActive: e.target.checked
                          })}
                          name="isActive"
                          color="primary"
                        />
                      }
                      label="Active"
                    />
                  </Grid>
                </>
              )}
            </Grid>
          </DialogContent>
          <DialogActions>
            <Button onClick={handleCloseEditDialog} disabled={savingEditDialog}>Cancel</Button>
            {isAdminOrOperator && (
              <Button
                onClick={handleRecalculateUsage}
                variant="outlined"
                disabled={savingEditDialog || recalculateUsageSaving || !deviceId}
              >
                {recalculateUsageSaving ? 'Recalculating...' : 'Recalculate Usage'}
              </Button>
            )}
            <Button onClick={handleSaveEditDevice} variant="contained" color="primary" disabled={savingEditDialog}>
              Save Changes
            </Button>
          </DialogActions>
        </Dialog>

        <Dialog
          open={multiplierPromptOpen}
          onClose={handleCloseMultiplierPrompt}
          fullScreen={isMobile}
          fullWidth
          maxWidth="sm"
          scroll="paper"
          PaperProps={{
            sx: {
              width: '100%',
              maxWidth: { xs: '100%', sm: 450 },
              m: { xs: 0, sm: 2 },
              maxHeight: { xs: '100dvh', sm: 'calc(100vh - 64px)' }
            }
          }}
        >
          <DialogTitle>Recalculate Previous Usage?</DialogTitle>
          <DialogContent>
            <DialogContentText>
              You changed the multiplier. This affects how usage is calculated from raw readings.
            </DialogContentText>
            <Box sx={{ mt: 2, p: 1.5, border: '1px solid', borderColor: 'divider', borderRadius: 1 }}>
              <Typography variant="subtitle2">Preview (latest 2 readings)</Typography>
              {multiplierPreview.loading ? (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 1 }}>
                  <CircularProgress size={16} />
                  <Typography variant="body2" color="text.secondary">Loading preview…</Typography>
                </Box>
              ) : multiplierPreview.error ? (
                <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                  {multiplierPreview.error}
                </Typography>
              ) : multiplierPreview.rows.length > 0 ? (
                <Box sx={{ mt: 1 }}>
                  {multiplierPreview.rows.map((row, index) => (
                    <Box key={`${row.toReadingTime}-${index}`} sx={{ display: 'grid', gap: 0.25 }}>
                      <Typography variant="caption" color="text.secondary">
                        From {new Date(row.fromReadingTime).toLocaleString()} ({row.fromReadingValue.toFixed(3)})
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        To {new Date(row.toReadingTime).toLocaleString()} ({row.toReadingValue.toFixed(3)})
                      </Typography>
                      <Typography variant="body2">
                        Raw usage: {row.rawUsage.toFixed(3)}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        Old result ({multiplierPreview.previousMultiplier}x): {row.previousUsage.toFixed(3)}
                      </Typography>
                      <Typography variant="body2" color="primary.main" sx={{ fontWeight: 600 }}>
                        New result ({multiplierPreview.nextMultiplier}x): {row.nextUsage.toFixed(3)}
                      </Typography>
                    </Box>
                  ))}
                </Box>
              ) : (
                <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                  No preview available.
                </Typography>
              )}
            </Box>
            <Box sx={{ mt: 2 }}>
              <FormControl fullWidth>
                <InputLabel id="multiplier-apply-mode-label" shrink>Apply change</InputLabel>
                <Select
                  labelId="multiplier-apply-mode-label"
                  value={multiplierApplyMode}
                  label="Apply change"
                  onChange={(e) => setMultiplierApplyMode(e.target.value)}
                  displayEmpty
                  renderValue={(selected) => {
                    if (!selected) return 'Select an option…';
                    if (selected === 'forward') return 'No — only apply from today onward';
                    if (selected === 'retroactive') return 'Yes — recalculate from earliest reading';
                    if (selected === 'custom') return 'Select a specific date';
                    return String(selected);
                  }}
                >
                  <MenuItem value="" disabled>Select an option…</MenuItem>
                  <MenuItem value="forward">No — only apply from today onward</MenuItem>
                  <MenuItem value="retroactive">Yes — recalculate from earliest reading</MenuItem>
                  <MenuItem value="custom">Select a specific date</MenuItem>
                </Select>
              </FormControl>
              {multiplierApplyMode === 'custom' && (
                <Box sx={{ mt: 2 }}>
                  <TextField
                    fullWidth
                    type="date"
                    label="Effective From Date"
                    value={multiplierCustomDate}
                    onChange={(e) => setMultiplierCustomDate(e.target.value)}
                    InputLabelProps={{ shrink: true }}
                  />
                </Box>
              )}
              {!!multiplierApplyMode && (
                <Box sx={{ mt: 2 }}>
                  <TextField
                    fullWidth
                    type="date"
                    label="Effective To Date (optional)"
                    value={multiplierCustomEndDate}
                    onChange={(e) => setMultiplierCustomEndDate(e.target.value)}
                    InputLabelProps={{ shrink: true }}
                  />
                </Box>
              )}
              <Box sx={{ mt: 1 }}>
                <Typography variant="body2" color="text.secondary">
                  From today: {new Date().toISOString().split('T')[0]}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Earliest reading: {multiplierEarliestDate || 'Unknown'}
                </Typography>
              </Box>
            </Box>
          </DialogContent>
          <DialogActions>
            <Button onClick={handleCloseMultiplierPrompt} disabled={multiplierPromptSaving}>Cancel</Button>
            <Button onClick={handleConfirmMultiplierPrompt} variant="contained" disabled={multiplierPromptSaving || !multiplierApplyMode}>Apply</Button>
          </DialogActions>
        </Dialog>

        <Dialog
          open={deleteDialogOpen}
          onClose={handleDeleteDialogClose}
          fullScreen={isMobile}
          scroll="paper"
          maxWidth="xs"
          fullWidth
          PaperProps={{
            sx: {
              maxHeight: { xs: '100dvh', sm: 'calc(100vh - 64px)' }
            }
          }}
        >
          <DialogTitle>Delete Device</DialogTitle>
          <DialogContent>
            <DialogContentText>
              Are you sure you want to delete this device? This action cannot be undone.
            </DialogContentText>
          </DialogContent>
          <DialogActions>
            <Button onClick={handleDeleteDialogClose}>Cancel</Button>
            <Button onClick={handleDeleteDevice} variant="contained" color="error">Delete</Button>
          </DialogActions>
        </Dialog>
      </Card>
      </Box>
    </Box>
  );
};

export default DeviceDetails;
